import { runAiJob, pollAiJob, rememberAiJob, forgetAiJob, recalledAiJobs } from "../lib/aiJob";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { theme, card, countTag, emptyFrame, floatingSheet } from "../theme";
import { RulerProgress, StatusTag, type StatusTone } from "../components/Drawing";
import { api } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { FRAMEWORK_LABELS } from "../context/FrameworksContext";
import { COUNTRIES, ALL_REGULATIONS, regulationsForCountries } from "../data/countriesData";
import { CI_SECTORS, CI_SECTOR_PARENTS, subSectors } from "../data/sectorsData";
import { allowsFreeText, isYesNoOptions, toggleWithExclusivity } from "../lib/intakeOptions";

// ── Types ─────────────────────────────────────────────────────────────────

interface Question {
  id: string;
  frameworkId: string | null;
  regulation: string | null;
  section: string;
  questionText: string;
  helpText: string | null;
  sortOrder: number;
  questionType: "TEXT" | "SINGLE_CHOICE" | "MULTI_CHOICE";
  options: string | null;
  orgProfileField: string | null;
}

/** Without controls:write (what every intake write needs on the server) the answers are shown read-only. */
const ReadOnlyContext = createContext(false);

interface AnswerState {
  text: string;
  saving: boolean;
  saved: boolean;
  /** Why the last save was refused, shown beside the answer. */
  error?: string;
  /** Shown from the organisation profile (GET /questions `prefilled`), not yet saved here. */
  fromProfile?: boolean;
}

interface AssessHint {
  controlId: string;
  controlName: string;
  framework: string;
  frameworkLabel: string;
  status: "compliant" | "in_progress" | "gap";
  confidence: "high" | "medium" | "low";
  summary: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function parseOptions(raw: string | null): string[] {
  if (!raw) return [];
  try { return JSON.parse(raw) as string[]; } catch { return []; }
}

function parseJsonArray(raw: string): string[] {
  if (!raw.trim()) return [];
  try { return JSON.parse(raw) as string[]; } catch { return []; }
}

function hasRealAnswer(text: string): boolean {
  if (!text.trim()) return false;
  const lines = text.split("\n").filter(Boolean);
  return lines.some((l) => l !== "Other: " && (l.startsWith("Other: ") ? l.slice(7).trim().length > 0 : true));
}

// Group key: the framework, "law:<name>" for a law-specific question, or the general block.
function groupKey(q: Pick<Question, "frameworkId" | "regulation">): string {
  return q.frameworkId ?? (q.regulation ? `law:${q.regulation}` : "__general__");
}

function groupByFrameworkAndSection(questions: Question[]): Map<string, Map<string, Question[]>> {
  const result = new Map<string, Map<string, Question[]>>();
  for (const q of questions) {
    const fw = groupKey(q);
    if (!result.has(fw)) result.set(fw, new Map());
    const sections = result.get(fw)!;
    if (!sections.has(q.section)) sections.set(q.section, []);
    sections.get(q.section)!.push(q);
  }
  return result;
}

const FRAMEWORK_ORDER = [
  "__general__", "iso27001", "cyber_essentials", "cyber_essentials_plus", "iso42001", "iso9001", "iso45001", "soc2", "pci_dss",
  "iasme_ca_sole_trader", "iasme_ca_micro", "iasme_ca_small", "iasme_ca_medium_large", "nist_csf", "iso14001", "iwa48", "ncsc_caf",
];

// Question sets for laws that are keyed by a framework ID (they predate law-keyed intake); they are
// listed with the other applicable laws, ahead of the "law:<name>" groups.
const LAW_FRAMEWORK_KEYS = ["gdpr", "uk_gdpr", "jersey_dpjl"];
const isLawGroup = (key: string) => key.startsWith("law:") || LAW_FRAMEWORK_KEYS.includes(key);

// Jersey DPJL is not an enrollable FrameworkId (it is derived from the org's regulations), so it
// has no entry in FRAMEWORK_LABELS. General questions are already filtered server-side to the
// org's own standards, so the group is simply "General".
const EXTRA_LABELS: Record<string, string> = {
  __general__: "General",
  jersey_dpjl: "Data Protection (Jersey) Law 2018",
};

function frameworkLabel(fw: string): string {
  if (fw.startsWith("law:")) return fw.slice(4);
  return EXTRA_LABELS[fw] ?? FRAMEWORK_LABELS[fw as keyof typeof FRAMEWORK_LABELS] ?? fw;
}

// Answers to these questions change which questions apply, so the list is refetched after they
// save: geography and regulations decide the laws, and the sector reveals the essential-services
// follow-ups (ComplianceQuestion.sectors — see backend lib/intakeScope.ts).
const SCOPE_PROFILE_FIELDS = new Set(["geography", "regulations", "sectorCode", "dpProfile.targetedCountries"]);

const STATUS_META: Record<AssessHint["status"], { label: string; color: string; tone: StatusTone }> = {
  compliant:   { label: "Likely Compliant", color: theme.color.successText, tone: "met" },
  in_progress: { label: "In Progress",      color: theme.color.warningText, tone: "partial" },
  gap:         { label: "Gap",              color: theme.color.dangerText,  tone: "gap" },
};

const CONFIDENCE_LABEL: Record<AssessHint["confidence"], string> = {
  high: "High confidence", medium: "Medium confidence", low: "Low confidence",
};

// ── Page ──────────────────────────────────────────────────────────────────

export function ComplianceQuestionsPage() {
  const { user } = useCurrentUser();
  const readOnly = !!user && !user.permissions?.includes("controls:write");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const [saveAllDone, setSaveAllDone] = useState(false);
  const [saveAllError, setSaveAllError] = useState<string | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [assessHints, setAssessHints] = useState<AssessHint[] | null>(null);
  const [assessError, setAssessError] = useState<string | null>(null);
  const [controlsUpdated, setControlsUpdated] = useState<number | null>(null);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const questionsRef = useRef<Question[]>([]);
  questionsRef.current = questions;

  useEffect(() => {
    api.get("/questions")
      .then((raw) => {
        const data = raw as { questions: Question[]; answers: Record<string, { answerText: string }>; prefilled?: string[] };
        setQuestions(data.questions);
        const fromProfile = new Set(data.prefilled ?? []);
        const initial: Record<string, AnswerState> = {};
        for (const [qId, ans] of Object.entries(data.answers)) {
          initial[qId] = { text: ans.answerText, saving: false, saved: true, fromProfile: fromProfile.has(qId) };
        }
        setAnswers(initial);
      })
      .catch(() => setError("Failed to load questions. Please refresh the page."))
      .finally(() => setLoading(false));
  }, []);

  // Re-fetch the list after a scope-changing answer so the sets for newly applicable laws
  // appear (and ones that no longer apply disappear) without a reload. Existing answer
  // state is kept; answers the server already holds for newly shown questions are merged in.
  const refreshQuestions = useCallback(async () => {
    try {
      const data = await api.get("/questions") as { questions: Question[]; answers: Record<string, { answerText: string }>; prefilled?: string[] };
      setQuestions(data.questions);
      const fromProfile = new Set(data.prefilled ?? []);
      setAnswers((prev) => {
        const next = { ...prev };
        for (const [qId, ans] of Object.entries(data.answers)) {
          if (!next[qId]) next[qId] = { text: ans.answerText, saving: false, saved: true, fromProfile: fromProfile.has(qId) };
        }
        return next;
      });
    } catch { /* the current list stays; a reload shows the new sets */ }
  }, []);

  const saveAnswer = useCallback(async (questionId: string, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], saving: true, saved: false } }));
    try {
      await api.put(`/questions/answers/${questionId}`, { answerText: text });
      setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], saving: false, saved: true, error: undefined } }));
      const field = questionsRef.current.find((q) => q.id === questionId)?.orgProfileField;
      if (field && SCOPE_PROFILE_FIELDS.has(field)) await refreshQuestions();
    } catch (e) {
      // Say so beside the answer: the box otherwise keeps the edited text as if it were saved.
      const error = e instanceof Error && e.message ? e.message : "Not saved";
      setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], saving: false, saved: false, error } }));
    }
  }, [refreshQuestions]);

  const handleChange = useCallback((questionId: string, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { text, saving: false, saved: false, fromProfile: false } }));
    clearTimeout(saveTimers.current[questionId]);
    saveTimers.current[questionId] = setTimeout(() => saveAnswer(questionId, text), 800);
  }, [saveAnswer]);

  const handleBlur = useCallback((questionId: string, text: string) => {
    clearTimeout(saveTimers.current[questionId]);
    saveAnswer(questionId, text);
  }, [saveAnswer]);

  const handleSaveAll = useCallback(async () => {
    setSaveAllLoading(true);
    setSaveAllDone(false);
    setSaveAllError(null);
    // Only what is not yet stored: an answer already saved is never re-sent, so a write-through
    // answer (the countries, the sector) can never put back a value changed on My Organisation since.
    const payload: Record<string, string> = {};
    for (const [qId, ans] of Object.entries(answers)) {
      if (!ans.saved || ans.fromProfile) payload[qId] = ans.text;
    }
    try {
      await api.post("/questions/save-all", { answers: payload });
      setSaveAllDone(true);
      setAnswers((prev) => {
        const next = { ...prev };
        for (const qId of Object.keys(next)) {
          next[qId] = { ...next[qId], saved: true };
        }
        return next;
      });
      setTimeout(() => setSaveAllDone(false), 3000);
    } catch (e) {
      setSaveAllError(e instanceof Error && e.message ? e.message : "Save failed. Please try again.");
    } finally {
      setSaveAllLoading(false);
    }
  }, [answers]);

  type AssessResult = { controlHints?: AssessHint[]; hints?: AssessHint[]; message?: string; controlsUpdated?: number };
  const ASSESS_KEY = "intake-assess";
  const assessStatusPath = (jobId: string) => `/questions/assess/jobs/${encodeURIComponent(jobId)}`;

  // The assessment runs on the worker; this waits for the job, which carries on if the page is left.
  const followAssess = useCallback(async (wait: Promise<AssessResult>) => {
    setAssessing(true);
    setAssessError(null);
    try {
      const result = await wait;
      setAssessHints(result.controlHints ?? result.hints ?? []);
      setControlsUpdated(result.controlsUpdated ?? 0);
    } catch (e) {
      setAssessError(e instanceof Error && e.message ? e.message : "Assessment failed. Please try again.");
    } finally {
      forgetAiJob(ASSESS_KEY);
      setAssessing(false);
    }
  }, []);

  const handleAssess = useCallback(async () => {
    setAssessHints(null);
    setControlsUpdated(null);
    await followAssess(runAiJob<AssessResult>("/questions/assess", assessStatusPath, {}, {
      onJobId: (jobId) => rememberAiJob(ASSESS_KEY, jobId),
    }));
  }, [followAssess]);

  // Picks up an assessment started before the page was left.
  useEffect(() => {
    const [saved] = recalledAiJobs(ASSESS_KEY);
    if (saved) void followAssess(pollAiJob<AssessResult>(saved.jobId, assessStatusPath, { waitMs: saved.remainingMs }));
  }, [followAssess]);

  const answered = questions.filter((q) => hasRealAnswer(answers[q.id]?.text ?? "")).length;
  const total = questions.length;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;

  const grouped = groupByFrameworkAndSection(questions);
  // General, then the standards in a fixed order, then every applicable law in the order seeded.
  const sortedFrameworks = [...FRAMEWORK_ORDER, ...LAW_FRAMEWORK_KEYS].filter((fw) => grouped.has(fw));
  for (const fw of grouped.keys()) {
    if (!sortedFrameworks.includes(fw)) sortedFrameworks.push(fw);
  }

  return (
    <div className="page-pad">

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px", margin: 0, marginBottom: 6 }}>
          About your business
        </h1>
        <p style={{ fontSize: 13, color: theme.color.muted, margin: 0, lineHeight: 1.6 }}>
          Your answers build your organisation's AI context — used to generate tailored policies, compliance checks, and guidance.
          They also pre-fill your <strong style={{ color: theme.color.text }}>My Organisation</strong> profile where applicable.
          Answers are saved automatically.
        </p>
      </div>

      {/* Progress bar */}
      {!loading && !error && (
        <div style={{ ...card, padding: "16px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Progress</span>
              <span style={{ fontSize: 12, color: theme.color.muted }}>{answered} of {total} answered</span>
            </div>
            <RulerProgress value={pct} label="Questions answered" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: pct === 100 ? theme.color.success : theme.color.accentText, minWidth: 48, textAlign: "right" }}>
            {pct}%
          </div>
        </div>
      )}

      {loading && (
        <div style={{ fontSize: 13, color: theme.color.muted, padding: "40px 0", textAlign: "center" }}>
          Loading questions…
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13, color: theme.color.dangerText, padding: "16px", background: theme.color.dangerBg, borderRadius: 3 }}>
          {error}
        </div>
      )}

      {readOnly && !loading && !error && (
        <div style={{ ...card, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: theme.color.muted }}>
          Read only: your role can see these answers but not change them (that needs the Edit controls permission).
        </div>
      )}

      <ReadOnlyContext.Provider value={readOnly}>
      <fieldset disabled={readOnly} style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
      {/* Questions grouped by framework and section; law-specific sets follow under their own divider */}
      {!loading && !error && sortedFrameworks.map((fw, i) => {
        const sections = grouped.get(fw)!;
        const firstLaw = isLawGroup(fw) && !isLawGroup(sortedFrameworks[i - 1] ?? "");
        return (
          <div key={fw} style={{ marginBottom: 32 }}>
            {firstLaw && (
              <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 20, marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Applicable laws</div>
                <div style={{ fontSize: 12, color: theme.color.muted, lineHeight: 1.5 }}>
                  Based on the countries you operate in and any regulations you added above. Change those answers and this list updates.
                </div>
              </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
              {frameworkLabel(fw)}
            </div>
            {[...sections.entries()].map(([section, qs]) => (
              <SectionBlock
                key={section}
                section={section}
                questions={qs}
                answers={answers}
                allAnswers={answers}
                allQuestions={questions}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            ))}
          </div>
        );
      })}

      </fieldset>
      </ReadOnlyContext.Provider>

      {!loading && !error && questions.length === 0 && (
        <div style={{ ...emptyFrame, padding: "40px 24px", color: theme.color.muted, fontSize: 13 }}>
          No questions available yet. Enrol in a compliance standard via My Services to see relevant intake questions.
        </div>
      )}

      {/* Save all + Assess buttons */}
      {!loading && !error && questions.length > 0 && !readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, marginBottom: 32, flexWrap: "wrap" }}>
          <button
            onClick={handleSaveAll}
            disabled={saveAllLoading}
            style={{
              padding: "10px 20px",
              background: theme.color.accent,
              color: "#fff",
              border: "none",
              borderRadius: 3,
              fontSize: 13,
              fontWeight: 600,
              cursor: saveAllLoading ? "not-allowed" : "pointer",
              opacity: saveAllLoading ? 0.7 : 1,
              fontFamily: "inherit",
            }}
          >
            {saveAllLoading ? "Saving…" : "Save all answers"}
          </button>
          {saveAllDone && (
            <span style={{ fontSize: 12, color: theme.color.success, fontWeight: 600 }}>All answers saved</span>
          )}
          {saveAllError && (
            <span style={{ fontSize: 12, color: "var(--c-dangerText)", fontWeight: 600 }}>{saveAllError}</span>
          )}

          <button
            onClick={handleAssess}
            disabled={assessing || answered === 0}
            style={{
              padding: "10px 20px",
              background: "none",
              color: theme.color.accentText,
              border: `1.5px solid ${theme.color.accent}`,
              borderRadius: 3,
              fontSize: 13,
              fontWeight: 600,
              cursor: (assessing || answered === 0) ? "not-allowed" : "pointer",
              opacity: (assessing || answered === 0) ? 0.5 : 1,
              fontFamily: "inherit",
            }}
          >
            {assessing ? "Assessing…" : "Assess compliance from intake"}
          </button>
          {answered === 0 && (
            <span style={{ fontSize: 11, color: theme.color.muted }}>Answer some questions first</span>
          )}
          {assessing && (
            <span style={{ fontSize: 11, color: theme.color.muted }}>Working through your answers. You can leave this page; the result will be here when you return.</span>
          )}
        </div>
      )}

      {assessError && (
        <div style={{ fontSize: 13, color: theme.color.dangerText, padding: "12px 16px", background: theme.color.dangerBg, borderRadius: 3, marginBottom: 24 }}>
          {assessError}
        </div>
      )}

      {/* Assessment results */}
      {assessHints !== null && (
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>
            Compliance Assessment from Intake
          </div>
          {controlsUpdated !== null && controlsUpdated > 0 && (
            <div style={{ fontSize: 13, color: theme.color.successText, padding: "10px 16px", background: theme.color.successBg, borderRadius: 3, marginBottom: 14, border: `1px solid ${theme.color.success}` }}>
              {controlsUpdated} control{controlsUpdated !== 1 ? "s" : ""} pre-populated across your enrolled standards based on your intake answers.
            </div>
          )}
          {assessHints.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "20px 24px", fontSize: 13, color: theme.color.muted }}>
              No controls could be inferred from your answers. Try answering more specific questions and running the assessment again.
            </div>
          ) : (
            assessHints.map((hint, i) => {
              const meta = STATUS_META[hint.status];
              return (
                <div key={i} style={{ ...card, marginBottom: 8, padding: "12px 16px", borderLeft: `3px solid ${meta.color}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, flex: 1, minWidth: 0 }}>{hint.controlName}</span>
                    <span style={{ fontSize: 11, color: theme.color.muted, background: theme.color.border, padding: "2px 7px", borderRadius: 2, whiteSpace: "nowrap" }}>
                      {hint.frameworkLabel || hint.framework}
                    </span>
                    <StatusTag tone={meta.tone}>{meta.label}</StatusTag>
                  </div>
                  <p style={{ fontSize: 12, color: theme.color.text, margin: 0, lineHeight: 1.6 }}>{hint.summary}</p>
                  <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4 }}>
                    {CONFIDENCE_LABEL[hint.confidence]} · {hint.controlId}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Section block ─────────────────────────────────────────────────────────

function SectionBlock({
  section,
  questions,
  answers,
  allAnswers,
  allQuestions,
  onChange,
  onBlur,
}: {
  section: string;
  questions: Question[];
  answers: Record<string, AnswerState>;
  allAnswers: Record<string, AnswerState>;
  allQuestions: Question[];
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const answered = questions.filter((q) => hasRealAnswer(answers[q.id]?.text ?? "")).length;

  return (
    <div style={{ ...card, marginBottom: 12, padding: 0, overflow: "hidden" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          borderBottom: open ? `1px solid ${theme.color.border}` : "none",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{section}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ ...countTag, padding: "0 6px" }}>
            {answered}/{questions.length}
          </span>
          <span style={{ fontSize: 11, color: theme.color.faint }}>{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "12px 18px 18px" }}>
          {questions.map((q, i) => (
            <QuestionItem
              key={q.id}
              question={q}
              state={answers[q.id]}
              allAnswers={allAnswers}
              allQuestions={allQuestions}
              onChange={onChange}
              onBlur={onBlur}
              last={i === questions.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Individual question item ───────────────────────────────────────────────

function QuestionItem({
  question,
  state,
  allAnswers,
  allQuestions,
  onChange,
  onBlur,
  last,
}: {
  question: Question;
  state: AnswerState | undefined;
  allAnswers: Record<string, AnswerState>;
  allQuestions: Question[];
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
  last: boolean;
}) {
  const text = state?.text ?? "";
  const hasAnswer = hasRealAnswer(text);
  const readOnly = useContext(ReadOnlyContext);

  function handleClear() {
    onChange(question.id, "");
    onBlur(question.id, "");
  }

  const options = parseOptions(question.options);
  // Operating Locations and the countries targeted without an establishment are both country lists.
  const isGeo = question.orgProfileField === "geography" || question.orgProfileField === "dpProfile.targetedCountries";
  const isRegs = question.orgProfileField === "regulations";
  const isSector = question.orgProfileField === "sectorCode";
  const isYesNo = question.questionType === "SINGLE_CHOICE" && isYesNoOptions(options);

  // Find geography question ID for regulations picker cross-reference
  const geoQuestion = allQuestions.find((q) => q.orgProfileField === "geography");
  const geoAnswerText = geoQuestion ? (allAnswers[geoQuestion.id]?.text ?? "") : "";

  return (
    <div style={{ paddingBottom: last ? 0 : 18, marginBottom: last ? 0 : 18, borderBottom: last ? "none" : `1px solid ${theme.color.border}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: hasAnswer ? theme.color.success : theme.color.border,
          marginTop: 5,
          flexShrink: 0,
          transition: "background 0.2s",
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.5, fontWeight: 500 }}>
            {question.questionText}
          </div>
          {question.helpText && (
            <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.5, marginTop: 3 }}>
              {question.helpText}
            </div>
          )}
          {state?.fromProfile && (
            <div style={{ fontSize: 11, color: theme.color.accentText, lineHeight: 1.5, marginTop: 3 }}>
              Filled in from your organisation profile - change it here if it is not right.
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {state?.saving && (
            <span style={{ fontSize: 11, color: theme.color.faint }}>Saving…</span>
          )}
          {state?.saved && !state.saving && hasAnswer && (
            <span style={{ fontSize: 11, color: theme.color.success }}>Saved</span>
          )}
          {state?.error && !state.saving && (
            <span role="alert" style={{ fontSize: 11, color: theme.color.dangerText }}>Not saved: {state.error}</span>
          )}
          {hasAnswer && !state?.saving && !readOnly && (
            <button onClick={handleClear} style={{ background: "none", border: "none", fontSize: 11, color: theme.color.faint, cursor: "pointer", textDecoration: "underline" }}>Clear</button>
          )}
        </div>
      </div>

      {isGeo ? (
        <CountryPickerQuestion question={question} state={state} onChange={onChange} onBlur={onBlur} />
      ) : isSector ? (
        <SectorPickerQuestion question={question} state={state} onChange={onChange} onBlur={onBlur} />
      ) : isRegs ? (
        <RegulationsPickerQuestion question={question} state={state} geoAnswerText={geoAnswerText} onChange={onChange} onBlur={onBlur} />
      ) : isYesNo ? (
        <YesNoWithDetailInput question={question} state={state} onChange={onChange} onBlur={onBlur} />
      ) : question.questionType === "SINGLE_CHOICE" ? (
        <SingleChoiceInput question={question} state={state} onChange={onChange} onBlur={onBlur} />
      ) : question.questionType === "MULTI_CHOICE" ? (
        <MultiChoiceInput question={question} state={state} onChange={onChange} onBlur={onBlur} />
      ) : (
        <textarea
          value={text}
          onChange={(e) => onChange(question.id, e.target.value)}
          onBlur={(e) => onBlur(question.id, e.target.value)}
          placeholder="Type your answer here…"
          rows={3}
          style={{
            width: "calc(100% - 17px)",
            boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${hasAnswer ? theme.color.borderStrong : theme.color.border}`,
            borderRadius: 3,
            padding: "9px 12px",
            fontSize: 13,
            color: theme.color.text,
            resize: "vertical",
            outline: "none",
            lineHeight: 1.6,
            fontFamily: "inherit",
            marginLeft: 17,
            transition: "border-color 0.15s",
          }}
        />
      )}
    </div>
  );
}

// ── Regulated sector picker ────────────────────────────────────────────────
//
// The stored answer is the sector *id*, not its label, because the id is what the threshold
// engine matches and what syncs to Organization.sectorCode. The same grouped list is on My
// Organisation; both edit the one value, and the last edit wins wherever it was made.

function SectorPickerQuestion({ question, state, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const selected = state?.text ?? "";
  const description = selected ? CI_SECTORS.find((s) => s.id === selected)?.description : null;

  function pick(id: string) {
    onChange(question.id, id);
    onBlur(question.id, id);
  }

  return (
    <div style={{ marginLeft: 17 }}>
      <select
        value={selected}
        onChange={(e) => pick(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: "var(--c-inputBg)",
          border: `1px solid ${selected ? theme.color.borderStrong : theme.color.border}`,
          borderRadius: 3,
          padding: "9px 12px",
          fontSize: 13,
          color: theme.color.text,
          outline: "none",
          fontFamily: "inherit",
        }}
      >
        <option value="">— select a sector —</option>
        {CI_SECTOR_PARENTS.map((parent) => {
          const children = subSectors(parent.id);
          return children.length === 0 ? (
            <option key={parent.id} value={parent.id}>{parent.label}</option>
          ) : (
            <optgroup key={parent.id} label={parent.label}>
              <option value={parent.id}>{parent.label} (all)</option>
              {children.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </optgroup>
          );
        })}
      </select>
      {description && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 5, lineHeight: 1.5 }}>
          {description}
        </div>
      )}
    </div>
  );
}

// ── Country picker ─────────────────────────────────────────────────────────

function CountryPickerQuestion({ question, state, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const selected = parseJsonArray(state?.text ?? "");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()) && !selected.includes(c));

  function addCountry(country: string) {
    const next = [...selected, country];
    const text = JSON.stringify(next);
    onChange(question.id, text);
    onBlur(question.id, text);
    setSearch("");
  }

  function removeCountry(country: string) {
    const next = selected.filter((c) => c !== country);
    const text = next.length > 0 ? JSON.stringify(next) : "";
    onChange(question.id, text);
    onBlur(question.id, text);
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div style={{ marginLeft: 17 }} ref={containerRef}>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map((c) => (
            <span key={c} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: theme.color.accent + "18",
              color: theme.color.accentText,
              border: `1px solid ${theme.color.accent}40`,
              borderRadius: 3, padding: "3px 8px", fontSize: 12, fontWeight: 500,
            }}>
              {c}
              <button
                onClick={() => removeCountry(c)}
                style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, padding: 0, lineHeight: 1, fontSize: 13 }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? "Search countries…" : "Add another country…"}
          style={{
            width: "100%", boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${theme.color.border}`,
            borderRadius: 3, padding: "8px 12px",
            fontSize: 13, color: theme.color.text, fontFamily: "inherit", outline: "none",
          }}
        />
        {open && filtered.length > 0 && (
          <div style={{
            ...floatingSheet,
            position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 4,
            maxHeight: 220, overflowY: "auto",
          }}>
            {filtered.slice(0, 50).map((c) => (
              <button
                key={c}
                onMouseDown={(e) => { e.preventDefault(); addCountry(c); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "8px 12px", background: "none", border: "none",
                  fontSize: 13, color: theme.color.text, cursor: "pointer",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = theme.color.border; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = "none"; }}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Regulations picker ─────────────────────────────────────────────────────

function RegulationsPickerQuestion({ question, state, geoAnswerText, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  geoAnswerText: string;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const savedRegs = parseJsonArray(state?.text ?? "");
  const geoCountries = parseJsonArray(geoAnswerText);
  const autoRegs = regulationsForCountries(geoCountries);

  // Manual additions: anything in savedRegs beyond the auto-derived set
  const autoSet = new Set(autoRegs);
  const [manualRegs, setManualRegs] = useState<string[]>(() => savedRegs.filter((r) => !autoSet.has(r)));

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const allSelected = new Set([...autoRegs, ...manualRegs]);
  const filtered = ALL_REGULATIONS.filter((r) => r.toLowerCase().includes(search.toLowerCase()) && !allSelected.has(r));

  function syncSave(extras: string[]) {
    const combined = [...autoRegs, ...extras];
    const text = combined.length > 0 ? JSON.stringify(combined) : "";
    onChange(question.id, text);
    onBlur(question.id, text);
  }

  // Re-sync when geography changes: anything now auto-derived stops being a manual addition,
  // and the saved answer is rewritten only if the combined list actually differs from it. Never
  // on opening the page: viewing changes nothing (the countries as loaded are the baseline).
  const geoSeen = useRef(geoAnswerText);
  useEffect(() => {
    if (geoSeen.current === geoAnswerText) return;
    geoSeen.current = geoAnswerText;
    const newAuto = regulationsForCountries(geoCountries);
    const newAutoSet = new Set(newAuto);
    const keep = manualRegs.filter((r) => !newAutoSet.has(r));
    if (keep.length !== manualRegs.length) setManualRegs(keep);
    const combined = [...newAuto, ...keep];
    const text = combined.length > 0 ? JSON.stringify(combined) : "";
    if (text !== (state?.text ?? "")) {
      onChange(question.id, text);
      onBlur(question.id, text);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoAnswerText]);

  function addReg(reg: string) {
    const next = [...manualRegs, reg];
    setManualRegs(next);
    syncSave(next);
    setSearch("");
    setOpen(false);
  }

  function removeManualReg(reg: string) {
    const next = manualRegs.filter((r) => r !== reg);
    setManualRegs(next);
    syncSave(next);
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div style={{ marginLeft: 17 }} ref={containerRef}>
      {/* Auto-derived regulations */}
      {autoRegs.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 5, fontWeight: 500 }}>
            Auto-derived from your countries of operation:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {autoRegs.map((r) => (
              <span key={r} style={{
                display: "inline-flex", alignItems: "center",
                background: theme.color.border,
                color: theme.color.text,
                borderRadius: 3, padding: "3px 8px", fontSize: 11, fontWeight: 500,
              }}>
                {r}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Manual additions */}
      {manualRegs.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 5, fontWeight: 500 }}>
            Manually added:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {manualRegs.map((r) => (
              <span key={r} style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: theme.color.accent + "18",
                color: theme.color.accentText,
                border: `1px solid ${theme.color.accent}40`,
                borderRadius: 3, padding: "3px 8px", fontSize: 11, fontWeight: 500,
              }}>
                {r}
                <button onClick={() => removeManualReg(r)} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search to add more */}
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search to add a regulation manually…"
          style={{
            width: "100%", boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${theme.color.border}`,
            borderRadius: 3, padding: "8px 12px",
            fontSize: 13, color: theme.color.text, fontFamily: "inherit", outline: "none",
          }}
        />
        {open && filtered.length > 0 && (
          <div style={{
            ...floatingSheet,
            position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 4,
            maxHeight: 200, overflowY: "auto",
          }}>
            {filtered.slice(0, 40).map((r) => (
              <button
                key={r}
                onMouseDown={(e) => { e.preventDefault(); addReg(r); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "7px 12px", background: "none", border: "none",
                  fontSize: 12, color: theme.color.text, cursor: "pointer", fontFamily: "inherit",
                }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = theme.color.border; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = "none"; }}
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>

      {autoRegs.length === 0 && manualRegs.length === 0 && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
          Select countries above to auto-populate applicable regulations, or search to add manually.
        </div>
      )}
    </div>
  );
}

// ── Yes / No with optional device detail ───────────────────────────────────

function YesNoWithDetailInput({ question, state, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const savedText = state?.text ?? "";
  const isYes = savedText === "Yes" || savedText.startsWith("Yes\nDetails: ");
  const isNo = savedText === "No";
  const detailMatch = savedText.match(/^Yes\nDetails: ([\s\S]*)$/);
  const [detail, setDetail] = useState(detailMatch ? detailMatch[1] : "");

  function pick(val: "Yes" | "No") {
    if (val === "No") {
      onChange(question.id, "No");
      onBlur(question.id, "No");
    } else {
      const text = detail ? `Yes\nDetails: ${detail}` : "Yes";
      onChange(question.id, text);
      onBlur(question.id, text);
    }
  }

  function handleDetailChange(val: string) {
    setDetail(val);
    const text = val ? `Yes\nDetails: ${val}` : "Yes";
    onChange(question.id, text);
  }

  function handleDetailBlur(val: string) {
    const text = val ? `Yes\nDetails: ${val}` : "Yes";
    onBlur(question.id, text);
  }

  return (
    <div style={{ marginLeft: 17, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="radio" name={question.id} checked={isYes} onChange={() => pick("Yes")} style={{ accentColor: theme.color.accent, cursor: "pointer" }} />
          <span style={{ fontSize: 13, color: theme.color.text }}>Yes</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="radio" name={question.id} checked={isNo} onChange={() => pick("No")} style={{ accentColor: theme.color.accent, cursor: "pointer" }} />
          <span style={{ fontSize: 13, color: theme.color.text }}>No</span>
        </label>
      </div>
      {isYes && (
        <textarea
          value={detail}
          onChange={(e) => handleDetailChange(e.target.value)}
          onBlur={(e) => handleDetailBlur(e.target.value)}
          placeholder="Describe the device(s) — make, model, or type…"
          rows={2}
          style={{
            width: "100%", boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${theme.color.border}`,
            borderRadius: 3, padding: "8px 12px",
            fontSize: 13, color: theme.color.text,
            resize: "vertical", outline: "none", lineHeight: 1.6,
            fontFamily: "inherit",
          }}
        />
      )}
    </div>
  );
}

// ── Single choice input ────────────────────────────────────────────────────

function SingleChoiceInput({ question, state, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const options = parseOptions(question.options);
  const savedText = state?.text ?? "";
  const freeText = allowsFreeText(question, options);

  let selected = "";
  let otherText = "";
  if (savedText.startsWith("Other: ")) {
    selected = "Other";
    otherText = savedText.slice(7);
  } else if (options.includes(savedText)) {
    selected = savedText;
  } else if (savedText) {
    selected = "Other";
    otherText = savedText;
  }

  function pick(opt: string) {
    const text = opt === "Other" ? (otherText ? `Other: ${otherText}` : "") : opt;
    onChange(question.id, text);
    onBlur(question.id, text);
  }

  return (
    <div style={{ marginLeft: 17, display: "flex", flexDirection: "column", gap: 6 }}>
      {options.map((opt) => (
        <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="radio"
            name={question.id}
            checked={selected === opt}
            onChange={() => pick(opt)}
            style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.4 }}>{opt}</span>
        </label>
      ))}
      {freeText && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="radio"
            name={question.id}
            checked={selected === "Other"}
            onChange={() => pick("Other")}
            style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: theme.color.text }}>Other</span>
        </label>
      )}
      {freeText && selected === "Other" && (
        <input
          type="text"
          value={otherText}
          onChange={(e) => onChange(question.id, `Other: ${e.target.value}`)}
          onBlur={(e) => onBlur(question.id, `Other: ${e.target.value}`)}
          placeholder="Please specify…"
          autoFocus
          style={{
            marginLeft: 22,
            width: "calc(100% - 22px)",
            boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${theme.color.border}`,
            borderRadius: 3,
            padding: "6px 10px",
            fontSize: 13,
            color: theme.color.text,
            fontFamily: "inherit",
            outline: "none",
          }}
        />
      )}
    </div>
  );
}

// ── Multi-choice input ─────────────────────────────────────────────────────

function MultiChoiceInput({ question, state, onChange, onBlur }: {
  question: Question;
  state: AnswerState | undefined;
  onChange: (qId: string, text: string) => void;
  onBlur: (qId: string, text: string) => void;
}) {
  const options = parseOptions(question.options);
  const savedText = state?.text ?? "";
  const freeText = allowsFreeText(question, options);

  const lines = savedText ? savedText.split("\n").filter(Boolean) : [];
  const otherLine = lines.find((l) => l.startsWith("Other: "));
  const otherText = otherLine ? otherLine.slice(7) : "";
  const otherSelected = !!otherLine;
  const selectedSet = new Set(lines.filter((l) => !l.startsWith("Other: ")));

  function buildText(sel: Set<string>, other: string): string {
    const parts = [...sel];
    if (other) parts.push(`Other: ${other}`);
    return parts.join("\n");
  }

  function toggle(opt: string) {
    const next = toggleWithExclusivity(selectedSet, opt);
    const text = buildText(next, otherText);
    onChange(question.id, text);
    onBlur(question.id, text);
  }

  function toggleOther() {
    if (otherSelected) {
      const text = buildText(selectedSet, "");
      onChange(question.id, text);
      onBlur(question.id, text);
    } else {
      onChange(question.id, buildText(selectedSet, " ").replace("Other:  ", "Other: "));
    }
  }

  function handleOtherText(val: string) {
    onChange(question.id, buildText(selectedSet, val));
  }

  function handleOtherBlur(val: string) {
    onBlur(question.id, buildText(selectedSet, val));
  }

  return (
    <div style={{ marginLeft: 17, display: "flex", flexDirection: "column", gap: 6 }}>
      {options.map((opt) => (
        <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={selectedSet.has(opt)}
            onChange={() => toggle(opt)}
            style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0, width: 14, height: 14 }}
          />
          <span style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.4 }}>{opt}</span>
        </label>
      ))}
      {freeText && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={otherSelected}
            onChange={toggleOther}
            style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0, width: 14, height: 14 }}
          />
          <span style={{ fontSize: 13, color: theme.color.text }}>Other</span>
        </label>
      )}
      {freeText && otherSelected && (
        <input
          type="text"
          value={otherText}
          onChange={(e) => handleOtherText(e.target.value)}
          onBlur={(e) => handleOtherBlur(e.target.value)}
          placeholder="Please specify…"
          autoFocus
          style={{
            marginLeft: 22,
            width: "calc(100% - 22px)",
            boxSizing: "border-box",
            background: "var(--c-inputBg)",
            border: `1px solid ${theme.color.border}`,
            borderRadius: 3,
            padding: "6px 10px",
            fontSize: 13,
            color: theme.color.text,
            fontFamily: "inherit",
            outline: "none",
          }}
        />
      )}
    </div>
  );
}
